import { AgentMessageFormat, Event, Citation, Footnote, AgentTextMessage } from "./types";
import { mergeUnique } from "../../../../utils";

const extractPunctuationAndNewlines = (text: string) => {
    const endOfTextPunctuation = text.trimEnd().match(/[.!?]$/)?.[0] ?? '';
    const newlines = (text.match(/\n+$/) ?? [''])[0];
    const matchedLength = (endOfTextPunctuation + newlines).length;
    const textWithoutPunctuation =
        matchedLength === 0 ? text : text.slice(0, -matchedLength);

    return { endOfTextPunctuation, newlines, textWithoutPunctuation };
};

const getLastWordOrMarkdown = (mainText: string) => {
    // get markdown (*word word* or **word word** or ***word word***, for bold and italics) or none-markdown word
    const lastWord = mainText
        .trim()
        .match(/(\*{1,3}[\w\s]+?\*{1,3}|\w+)$/)?.[0];

    mainText = lastWord?.length
        ? mainText.slice(0, -lastWord.length)
        : mainText;

    return { lastWord, mainText };
};

export const getMarkdownTextElements = (
    text: string,
    citations: Citation[] = [],
    footnotes: Footnote[] = [],
) => {
    const {
        endOfTextPunctuation,
        newlines,
        textWithoutPunctuation,
    } = extractPunctuationAndNewlines(text);

    const { lastWord, mainText } = getLastWordOrMarkdown(
        textWithoutPunctuation,
    );

    const endOfTextData = {
        lastWord,
        citations,
        footnotes,
        endOfTextPunctuation,
    };
    const endOfText =
        text.length > 1
            ? `<div data='${JSON.stringify(endOfTextData).replace(
                  /'/g,
                  '&#39;',
              )}'></div>`
            : '';

    return [...mainText.split(''), endOfText, newlines, ' '];
};

export const getHtmlTextElements = (
    html: string,
    citations: Citation[] = [],
    footnotes: Footnote[] = [],
) => {
    const textToRender = [];
    const processedHtml = [];

    // sometimes html is null
    html = html ?? '';

    // split by words
    // html tags are kept as one word
    // 'Find me on <a href="website.com">' => ['Find', ' ', 'me', ' ', 'on', '<a href="website.com">']
    const words = html.match(/<[^>]+>|[^<>\s]+|\s*/g) || [];
    if (words.length > 1) {
        processedHtml.push(...words);
    } else {
        processedHtml.push(html);
    }

    textToRender.push(...processedHtml);

    if (citations.length > 0 || footnotes.length > 0) {
        textToRender.push(
            `<span type='sources-container' data='${JSON.stringify({
                citations,
                footnotes,
            }).replace(/'/g, '&#39;')}'></span>`,
        );
    }

    return textToRender;
};

export const isAgentTextMessage = (payload: {
    [key: string]: any;
}): payload is AgentTextMessage => {
    return payload?.type === 'agent';
};

export const entryToTextMessage = (
    entry: Event,
    agentMessageFormat?: AgentMessageFormat,
) => {
    if (entry.user_action && entry.user_action.action_type === 'utterance') {
        return {
            type: 'user',
            actionId: entry.user_action.id,
            text: entry.user_action.payload.text,
        };
    } else if (
        entry.agent_action &&
        entry.agent_action.action_type === 'utterance'
    ) {
        if (entry.agent_action.payload.textual) {
            let displayText: string[] = [];
            const { chunks } = entry.agent_action.payload;
            if (chunks && chunks.length) {
                chunks.forEach(({ text, html, citations, footnotes }) => {
                    const textToRender =
                        agentMessageFormat === 'html'
                            ? getHtmlTextElements(html, citations, footnotes)
                            : getMarkdownTextElements(
                                  text,
                                  citations,
                                  footnotes,
                              );

                    displayText.push(...textToRender);
                });
            } else {
                displayText = entry.agent_action.payload.textual.text.split('');
            }
            return {
                type: 'agent',
                actionId: entry.agent_action.id,
                text: displayText,
                citations: entry.agent_action.payload.chunks
                    ?.map(c => c.citations ?? [])
                    .reduce(
                        (prev, curr) =>
                            mergeUnique(prev, curr, citation => citation.id),
                        [],
                    ),
                footnotes: entry.agent_action.payload.chunks
                    ?.map(c => c.footnotes ?? [])
                    .reduce(
                        (prev, curr) =>
                            mergeUnique(prev, curr, footnote => footnote.id),
                        [],
                    ),
                attachments: entry.agent_action.payload.media?.attachments,
            } as AgentTextMessage;
        }
    }
};

export const getCreatedAtDelta = (event: Event, fromEvent: Event) => {
    return (
        new Date(event.created_at).getTime() -
        new Date(fromEvent.created_at).getTime()
    );
};